import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  /** @param {Event} event */
  #handleVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    const updatedProductId = event.detail?.data?.productId;
    const currentProductId = this.dataset.productId;

    console.log('[MediaGallery] VariantUpdateEvent received | event product:', updatedProductId, '| gallery product:', currentProductId);

    if (updatedProductId && currentProductId && updatedProductId !== currentProductId) {
      console.log('[MediaGallery] Product ID mismatch — skipping update');
      return;
    }

    const source = event.detail.data.html;

    if (!source) {
      console.warn('[MediaGallery] No HTML in event detail');
      return;
    }

    // The gallery renders every media, so a colour that is already present is handled by the live filter.
    if (this.#hasMediaForSelectedColor(event)) {
      console.log('[MediaGallery] Selected colour already present in current gallery — keeping it');
      return;
    }

    const newMediaGallery =
      source.querySelector(`media-gallery[data-product-id="${updatedProductId}"]`) || source.querySelector('media-gallery');

    if (!(newMediaGallery instanceof HTMLElement)) {
      console.warn('[MediaGallery] No media-gallery element found in response HTML');
      return;
    }

    console.log('[MediaGallery] Updating gallery, hide-variants:', newMediaGallery.dataset.hideVariants);
    this.#filterMediaByVariant(newMediaGallery, event);
    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {VariantUpdateEvent} event
   * @returns {boolean}
   */
  #hasMediaForSelectedColor(event) {
    const selectedColorValue = this.#getSelectedColorFromDOM(event);
    if (!selectedColorValue) return false;

    const normalizedColor = this.#normalizeText(selectedColorValue);
    const slides = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('slideshow-slide')));

    return slides.some((slide) => this.#isColorMatch(this.#getMediaAltText(slide), normalizedColor));
  }

  /**
   * Filters the incoming media gallery content by the selected variant option values.
   * @param {HTMLElement} mediaGallery - The media gallery element from the updated HTML.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #filterMediaByVariant(mediaGallery, event) {
    const strictVariantMedia = mediaGallery.dataset.hideVariants === 'true';

    // Step 1: Get the selected color — prioritize click-time captured value.
    let selectedColorValue = this.#getSelectedColorFromDOM(event);

    if (!selectedColorValue) {
      selectedColorValue = this.#getSelectedColorFromVariantResource(event, mediaGallery);
    }

    console.log('[MediaGallery] Selected color value:', selectedColorValue || '(none)', '| strict mode:', strictVariantMedia);

    if (!selectedColorValue) {
      console.warn('[MediaGallery] No color detected — cannot filter media');
      return;
    }

    // Step 2: Normalize.
    let normalizedColor = this.#normalizeText(selectedColorValue);

    // Step 3: Collect all slides.
    const slideSelectors = ['slideshow-slide', 'li.product-media-container'].join(', ');

    const slides = /** @type {HTMLElement[]} */ (Array.from(mediaGallery.querySelectorAll(slideSelectors)));
    console.log('[MediaGallery] Total slides in new gallery:', slides.length);

    // Step 4: Match each slide alt text against the selected color.
    let matchingSlides = slides.filter((slide) => {
      const imageAlt = this.#getMediaAltText(slide);
      const match = this.#isColorMatch(imageAlt, normalizedColor);
      console.log(`[MediaGallery] Slide alt: "${imageAlt}" → ${match ? 'MATCH ✓' : 'no match ✗'}`);
      return match;
    });

    console.log('[MediaGallery] Matching slides:', matchingSlides.length, '/', slides.length);

    if (matchingSlides.length === 0) {
      const variantColor = this.#getSelectedColorFromVariantResource(event, mediaGallery);
      if (variantColor) {
        normalizedColor = this.#normalizeText(variantColor);
        matchingSlides = slides.filter((slide) => this.#isColorMatch(this.#getMediaAltText(slide), normalizedColor));
      }
    }

    if (matchingSlides.length === 0) {
      console.warn('[MediaGallery] No matching color metadata — preserving product media');
      return;
    }

    slides.forEach((slide) => {
      if (!matchingSlides.includes(slide)) {
        slide.remove();
      } else {
        slide.removeAttribute('hidden');
        slide.setAttribute('aria-hidden', 'false');
        slide.style.display = '';
      }
    });

    // Step 5: Filter thumbnails.
    const thumbnails = /** @type {HTMLElement[]} */ (
      Array.from(mediaGallery.querySelectorAll('.slideshow-controls__thumbnail'))
    );

    thumbnails.forEach((thumbnail) => {
      const imageAlt = this.#getMediaAltText(thumbnail);
      const isMatch = this.#isColorMatch(imageAlt, normalizedColor);
      console.log(`[MediaGallery] Thumbnail alt: "${imageAlt}" → ${isMatch ? 'MATCH ✓' : 'no match ✗'}`);
      if (!isMatch) {
        thumbnail.remove();
      } else {
        thumbnail.removeAttribute('hidden');
        thumbnail.setAttribute('aria-hidden', 'false');
        thumbnail.style.display = '';
      }
    });
  }

  /**
   * Reads media alt text from dataset or nested image.
   * @param {Element} element
   * @returns {string}
   */
  #getMediaAltText(element) {
    if (!(element instanceof HTMLElement)) return '';
    const datasetAlt = element.dataset.mediaAlt || '';
    if (datasetAlt.trim()) return datasetAlt.trim();

    const image = element.querySelector('img');
    const imageAlt = image?.getAttribute('alt') || '';
    return imageAlt.trim();
  }

  /**
   * Gets the selected color value directly from the color swatch input
   * @param {VariantUpdateEvent} event
   * @returns {string} The selected color value
   */
  #getSelectedColorFromDOM(event) {
    const productId = event.detail?.data?.productId;

    /** @param {Element | null | undefined} input */
    const getEnglishColorFromInput = (input) => {
      if (!(input instanceof HTMLInputElement)) return '';
      const swatch = input.closest('label')?.querySelector('.swatch[data-english-color]');
      const englishColor = swatch?.getAttribute('data-english-color')?.trim() || '';
      if (englishColor) {
        console.log('[MediaGallery] Color from swatch english data attribute:', englishColor);
      }
      return englishColor;
    };

    // PRIORITY 1: Color stored at click-time on the variant-picker element (most reliable).
    const pickerEl = productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelector('variant-picker');

    const pickerCheckedColorInput = pickerEl?.querySelector('fieldset[data-option-name="color"] input[type="radio"]:checked');
    const englishColorFromPickerInput = getEnglishColorFromInput(pickerCheckedColorInput);
    if (englishColorFromPickerInput) {
      return englishColorFromPickerInput;
    }

    if (pickerEl instanceof HTMLElement && pickerEl.dataset.selectedColor?.trim()) {
      const color = pickerEl.dataset.selectedColor.trim();
      console.log('[MediaGallery] Color from picker click-time store:', color);
      return color;
    }

    // PRIORITY 2: Checked radio in the live page color fieldset.
    const liveFieldset = document.querySelector(
      productId
        ? `variant-picker[data-product-id="${productId}"] fieldset[data-option-name="color"]`
        : 'fieldset[data-option-name="color"]'
    );
    const liveChecked = liveFieldset?.querySelector('input[type="radio"]:checked');

    const englishColorFromLiveInput = getEnglishColorFromInput(liveChecked);
    if (englishColorFromLiveInput) {
      return englishColorFromLiveInput;
    }

    if (liveChecked instanceof HTMLInputElement && liveChecked.value.trim()) {
      console.log('[MediaGallery] Color from live DOM fieldset:', liveChecked.value.trim());
      return liveChecked.value.trim();
    }

    // PRIORITY 3: Checked radio in the AJAX response HTML.
    const sourceDocument = event.detail?.data?.html;
    if (sourceDocument instanceof Document) {
      const responseFieldset = sourceDocument.querySelector('fieldset[data-option-name="color"]');
      const responseChecked = responseFieldset?.querySelector('input[type="radio"]:checked');

      const englishColorFromResponseInput = getEnglishColorFromInput(responseChecked);
      if (englishColorFromResponseInput) {
        return englishColorFromResponseInput;
      }

      if (responseChecked instanceof HTMLInputElement && responseChecked.value.trim()) {
        console.log('[MediaGallery] Color from AJAX response HTML:', responseChecked.value.trim());
        return responseChecked.value.trim();
      }
    }

    console.warn('[MediaGallery] Could not detect selected color from any source');
    return '';
  }

  /**
   * Fallback color detection from selected variant values when DOM hooks are unavailable.
   * Picks the variant option value that best matches image alt text in the incoming gallery.
   * @param {VariantUpdateEvent} event
   * @param {HTMLElement} mediaGallery
   * @returns {string}
   */
  #getSelectedColorFromVariantResource(event, mediaGallery) {
    const variant = /** @type {{ options?: string[]; option1?: string; option2?: string; option3?: string } | null} */ (
      event.detail?.resource || null
    );
    if (!variant) return '';

    /** @type {string[]} */
    const candidateValues = [];

    if (Array.isArray(variant.options)) {
      variant.options.forEach((value) => {
        if (typeof value === 'string' && value.trim()) {
          candidateValues.push(value.trim());
        }
      });
    }

    [variant.option1, variant.option2, variant.option3].forEach((value) => {
      if (typeof value === 'string' && value.trim()) {
        candidateValues.push(value.trim());
      }
    });

    const uniqueCandidates = [...new Set(candidateValues)];
    if (!uniqueCandidates.length) return '';

    const slideSelectors = ['slideshow-slide', 'li.product-media-container'].join(', ');
    const slides = /** @type {HTMLElement[]} */ (Array.from(mediaGallery.querySelectorAll(slideSelectors)));
    if (!slides.length) return '';

    let bestColor = '';
    let bestMatches = 0;

    uniqueCandidates.forEach((candidate) => {
      const normalizedCandidate = this.#normalizeText(candidate);
      const matches = slides.filter((slide) => this.#isColorMatch(this.#getMediaAltText(slide), normalizedCandidate)).length;

      if (matches > bestMatches) {
        bestMatches = matches;
        bestColor = candidate;
      }
    });

    if (bestColor) {
      console.log('[MediaGallery] Color from variant resource fallback:', bestColor, '| matches:', bestMatches);
    }

    return bestColor;
  }

  /**
   * @param {string} imageAlt
   * @param {string} normalizedColor
   * @returns {boolean}
   */
  #isColorMatch(imageAlt, normalizedColor) {
    const normalizedAlt = this.#normalizeText(imageAlt);

    // Exact match: "BLACK MULTI SUEDE" === "BLACK MULTI SUEDE"
    if (normalizedAlt === normalizedColor) return true;

    // Alt contains the full color phrase: "BLACK MULTI SUEDE SIDE1" includes "BLACK MULTI SUEDE"
    if (normalizedAlt.includes(normalizedColor)) return true;

    // Color phrase contains the full alt (e.g. alt is "BLACK" and color is "BLACK MULTI SUEDE")
    if (normalizedColor.includes(normalizedAlt) && normalizedAlt.length > 2) return true;

    return false;
  }

  /**
   * Normalizes string content for comparison.
   * @param {string} value - The string to normalize.
   * @returns {string}
   */
  #normalizeText(value) {
    return value
      .toString()
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * @param {string} value
   * @returns {string[]}
   */
  #normalizeWords(value) {
    return this.#normalizeText(value)
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .map((word) => word.trim())
      .filter(Boolean);
  }

  /**
   * @param {{ option1?: string, option2?: string, option3?: string, title?: string, options?: string[] } | null} variant
   * @returns {string[]}
   */
  #getVariantMatchValues(variant) {
    if (!variant || Array.isArray(variant)) return [];

    /** @type {string[]} */
    const values = [];

    [variant.option1, variant.option2, variant.option3, variant.title].forEach((value) => {
      if (typeof value === 'string' && value.trim()) {
        values.push(value.trim());
      }
    });

    if (Array.isArray(variant.options)) {
      variant.options.forEach((value) => {
        if (typeof value === 'string' && value.trim()) {
          values.push(value.trim());
        }
      });
    }

    return values;
  }

  #getSelectedVariantOptionValues() {
    const sectionRoot = this.closest('.shopify-section, dialog') || document;
    const optionElements = /** @type {HTMLInputElement[]} */ (
      Array.from(sectionRoot.querySelectorAll('.variant-picker__form input[name^="options["]'))
    );
    /** @type {string[]} */
    const selectedValues = [];

    for (const input of optionElements) {
      if (input.checked) {
        selectedValues.push(input.value.trim());
      }
    }

    if (selectedValues.length) {
      return selectedValues;
    }

    const selectedOptions = /** @type {HTMLOptionElement[]} */ (Array.from(
      sectionRoot.querySelectorAll('.variant-picker__form select[name^="options["] option[selected]')
    ));
    return selectedOptions.map((option) => option.value.trim()).filter((value) => value);
  }

  #getSelectedColorOptionValues() {
    const sectionRoot = this.closest('.shopify-section, dialog') || document;
    const colorNamePattern = /color|colour/i;
    /** @type {string[]} */
    const colorValues = [];

    // Method 1: Look for checked radio inputs in color fieldsets
    const colorFieldsets = Array.from(sectionRoot.querySelectorAll('fieldset')).filter((fieldset) => {
      const legend = fieldset.querySelector('legend');
      return legend && colorNamePattern.test(legend.textContent);
    });

    colorFieldsets.forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement && checkedInput.value.trim()) {
        colorValues.push(checkedInput.value.trim());
      }
    });

    // Method 2: Look for checked radio inputs with color-related names
    if (colorValues.length === 0) {
      const checkedInputs = /** @type {HTMLInputElement[]} */ (
        Array.from(sectionRoot.querySelectorAll('input[type="radio"]:checked'))
      );
      checkedInputs.forEach((input) => {
        if (input instanceof HTMLInputElement && colorNamePattern.test(input.name) && input.value.trim()) {
          colorValues.push(input.value.trim());
        }
      });
    }

    // Method 3: Look for selected color in select elements
    if (colorValues.length === 0) {
      const selects = /** @type {HTMLSelectElement[]} */ (Array.from(sectionRoot.querySelectorAll('select')));
      selects.forEach((select) => {
        if (!(select instanceof HTMLSelectElement)) return;
        if (!colorNamePattern.test(select.name)) return;

        const option = select.options[select.selectedIndex];
        if (option?.value.trim()) {
          colorValues.push(option.value.trim());
        }
      });
    }

    return colorValues;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  /** @param {Event} event */
  #handleZoomMediaSelected = async (event) => {
    if (!(event instanceof ZoomMediaSelectedEvent)) return;

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
